
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminUser } from '@/contexts/auth/types';
import { useToast } from '@/hooks/use-toast';
import { updateUser, getUserById } from '@/services/userService';
import { Loader2 } from 'lucide-react';
import { UserRoles, UserStatus } from '@/contexts/auth/enums';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface UserEditDialogProps {
  user: AdminUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserUpdated: () => void;
}

const UserEditDialog: React.FC<UserEditDialogProps> = ({
  user,
  open,
  onOpenChange,
  onUserUpdated
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState(user?.userProfile?.name?.firstName || '');
  const [lastName, setLastName] = useState(user?.userProfile?.name?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [role, setRole] = useState<UserRoles>(
    user?.roles && user.roles.length > 0 ? user.roles[0] : UserRoles.USER
  );
  const [status, setStatus] = useState<UserStatus>(
    user?.status || UserStatus.INACTIVE
  );
  
  const queryClient = useQueryClient();

  // Reset form when user changes
  useEffect(() => {
    const loadUserDetails = async () => {
      if (user && open) {
        setIsLoading(true);
        try {
          // Get fresh user data if we have an ID
          if (user['@id']) {
            const freshUserData = await getUserById(user['@id']);
            setFirstName(freshUserData.userProfile?.name?.firstName || '');
            setLastName(freshUserData.userProfile?.name?.lastName || '');
            setEmail(freshUserData.email || '');
            setRole(freshUserData.roles && freshUserData.roles.length > 0 ? freshUserData.roles[0] : UserRoles.USER);
            setStatus(freshUserData.status || UserStatus.INACTIVE);
          } else {
            // Just use the provided user data
            setFirstName(user.userProfile?.name?.firstName || '');
            setLastName(user.userProfile?.name?.lastName || '');
            setEmail(user.email || '');
            setRole(user.roles && user.roles.length > 0 ? user.roles[0] : UserRoles.USER);
            setStatus(user.status || UserStatus.INACTIVE);
          }
        } catch (error) {
          console.error('Error loading user details:', error);
          toast({
            title: "Error",
            description: "Failed to load user details.",
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadUserDetails();
  }, [user, open, toast]);

  const updateUserMutation = useMutation({
    mutationFn: (userData: { id: string, data: Partial<AdminUser> }) => 
      updateUser(userData.id, userData.data),
    onSuccess: () => {
      toast({
        title: "User updated",
        description: "User information has been successfully updated.",
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onUserUpdated();
      onOpenChange(false);
    },
    onError: (error) => {
      console.error('Error updating user:', error);
      toast({
        title: "Update failed",
        description: "There was an error updating the user.",
        variant: "destructive",
      });
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;

    try {
      const updatedUserData = {
        email,
        roles: [role], // Ensure roles is an array
        status,
        userProfile: {
          ...user.userProfile,
          name: {
            ...user.userProfile.name,
            firstName,
            lastName
          }
        }
      };

      updateUserMutation.mutate({ 
        id: user['@id'], 
        data: updatedUserData 
      });
    } catch (error) {
      console.error('Error preparing user data:', error);
      toast({
        title: "Update failed",
        description: "There was an error preparing the user data.",
        variant: "destructive",
      });
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="firstName" className="text-right">
                  First Name
                </Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="lastName" className="text-right">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="email" className="text-right">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="role" className="text-right">
                  Role
                </Label>
                <Select value={role} onValueChange={(value) => setRole(value as UserRoles)}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UserRoles.ADMIN}>Admin</SelectItem>
                    <SelectItem value={UserRoles.ORGANIZER}>Organizer</SelectItem>
                    <SelectItem value={UserRoles.USER}>User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="status" className="text-right">
                  Status
                </Label>
                <Select
                  value={status}
                  onValueChange={(value: UserStatus) => setStatus(value)}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UserStatus.ACTIVE}>Active</SelectItem>
                    <SelectItem value={UserStatus.INACTIVE}>Inactive</SelectItem>
                    <SelectItem value={UserStatus.SUSPENDED}>Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={updateUserMutation.isPending}>
                {updateUserMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserEditDialog;
